import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/Table';
import { ScoreChip } from '../common/ScoreChip';
import { RecordStatusPill, TrackPill } from '../common/StatusPills';
import { EmptyState } from '../common/EmptyState';
import { entityName } from '../../data/entities';
import { formatAed, daysUntil } from '../../utils/format';
import { STAGE_META } from '../../utils/stages';
import type { ApplicationRecord } from '../../types/domain';
interface ApplicationsTableProps {
  records: ApplicationRecord[];
}
export function ApplicationsTable({
  records
}: ApplicationsTableProps) {
  const navigate = useNavigate();
  if (records.length === 0) {
    return <EmptyState title="No records match these filters" description="Adjust the track, stage or search filters to widen the queue. Records are never removed — declined applications remain in the archive." data-id="element-338" data-mp="387qbwctf3hdp" />;
  }
  return <div className="overflow-x-auto rounded-sm border border-border bg-card" data-id="element-339" data-mp="3lmo5iach68up">
      <Table data-id="element-340" data-mp="3gsa5tvqs5fk8">
        <TableHeader data-id="element-341" data-mp="3gsjy5pzaryt3">
          <TableRow data-id="element-342" data-mp="3buru5xldom8j">
            <TableHead className="w-[150px]" data-id="element-343" data-mp="3bu8md9pfze88">Reference</TableHead>
            <TableHead className="min-w-[260px]" data-id="element-344" data-mp="3bsfexs708pz6">Event & entity</TableHead>
            <TableHead data-id="element-345" data-mp="36x8hv388qjj8">Track</TableHead>
            <TableHead data-id="element-346" data-mp="8e1h4yrdp8xc">Emirate</TableHead>
            <TableHead className="text-right" data-id="element-347" data-mp="8cqgh7t8chag">Support</TableHead>
            <TableHead data-id="element-348" data-mp="3f875gbaw01x">Stage</TableHead>
            <TableHead className="text-right" data-id="element-349" data-mp="3g9ryci4c0wu">AI score</TableHead>
            <TableHead data-id="element-350" data-mp="3ukqbz5ez9tpd">Status</TableHead>
            <TableHead data-id="element-351" data-mp="3ujoq05wfbmsv">Officer</TableHead>
            <TableHead className="text-right" data-id="element-352" data-mp="3pm6nax9023tb">SLA</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody data-id="element-353" data-mp="2j5up4w66glxq">
          {records.map(record => {
          const sla = daysUntil(record.slaDueOn);
          const breached = sla < 0 && record.status !== 'rejected';
          return <TableRow key={record.id} onClick={() => navigate(`/applications/${record.id}`)} className="cursor-pointer" data-id="element-354" data-mp="167u7cvbx9451">
                <TableCell className="font-mono text-[11px] text-muted-foreground" data-id="element-355" data-mp="w9d9rfih9ra0">
                  {record.ref}
                </TableCell>
                <TableCell data-id="element-356" data-mp="117eqhtsfleib">
                  <p className="text-[13px] font-medium leading-snug" data-id="element-357" data-mp="116mzyxdoy6yw">{record.eventName}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-358" data-mp="miaa8i575kcu">
                    {entityName(record.entityId)}
                  </p>
                </TableCell>
                <TableCell data-id="element-359" data-mp="djom87gyhd4o">
                  <TrackPill track={record.track} data-id="element-360" data-mp="dkgdj63isop5" />
                </TableCell>
                <TableCell className="text-[12px]" data-id="element-361" data-mp="ij1e56mopae4">{record.emirate}</TableCell>
                <TableCell className="text-right font-mono text-[12px]" data-id="element-362" data-mp="3k7ivwp6fbbv">
                  {formatAed(record.requestedSupportAed)}
                </TableCell>
                <TableCell data-id="element-363" data-mp="8lwvhgs1rx22">
                  <span className="text-[12px]" data-id="element-364" data-mp="3l3500wokeyct">{STAGE_META[record.stage].label}</span>
                  <span className="block text-[11px] text-muted-foreground" data-id="element-365" data-mp="3h1t64eynrqyt">
                    {record.daysInStage} days in stage
                  </span>
                </TableCell>
                <TableCell className="text-right" data-id="element-366" data-mp="3uz7xvqcdwaty">
                  <ScoreChip score={record.aiScore} data-id="element-367" data-mp="3q1g56u3x0kop" />
                </TableCell>
                <TableCell data-id="element-368" data-mp="7wcumtv6jggw">
                  <RecordStatusPill status={record.status} data-id="element-369" data-mp="7vjx8ji423bf" />
                </TableCell>
                <TableCell className="text-[12px]" data-id="element-370" data-mp="3u88eg729m4a">{record.officer}</TableCell>
                <TableCell className="text-right" data-id="element-371" data-mp="hn7jm5zt8u61">
                  {breached ? <span className="inline-flex items-center gap-1 font-mono text-[11px] text-uae-red" data-id="element-372" data-mp="cnuz3rwevz6m">
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" data-id="element-373" data-mp="rkc9afsv77rl" />
                      {Math.abs(sla)}d over
                    </span> : <span className="font-mono text-[11px] text-muted-foreground" data-id="element-374" data-mp="mjxy4ovyvhpc">
                      {record.status === 'rejected' ? '—' : `${sla}d`}
                    </span>}
                </TableCell>
              </TableRow>;
        })}
        </TableBody>
      </Table>
    </div>;
}